import { normalizeElementPath, type ElementPathNode, type RawElementPath } from '../../utils/element-path.utils';
import type { DriveSelectionTarget } from '../../drive/drive-context-state.utils';
import type { NotificationObject } from './notification.types';

/**
 * Org entities whose page hosts the drive feed anchored on the entity's own id. GLOBAL is an audience's notice
 * (conversation-api STUDIO-2574): it belongs to no brand and shows on every brand's feed, so it opens on the brand page.
 */
export const ORG_ELEMENT_TYPES: ReadonlySet<string> = new Set(['BRAND', 'WORKSPACE', 'CAMPAIGN', 'GLOBAL']);

/** Element types that never address a Drive directory. */
export const NON_DIRECTORY_ELEMENT_TYPES: ReadonlySet<string> = new Set([
  ...ORG_ELEMENT_TYPES,
  'PROJECT',
  'FILE',
  'PAGE',
  'SHARE-LINK',
]);

export interface OrgEntityIds {
  readonly brandId?: string;
  readonly workspaceId?: string;
  readonly campaignId?: string;
}

/**
 * Ids of the org entities a notification names. Org-level notifications carry the
 * entity only as `elementUuid` (no `workspaceId` / `campaignId` field) and an
 * `elementPath` of its ancestors, so each id falls back to those.
 */
export function orgEntityIds(obj: NotificationObject): OrgEntityIds {
  const path: Record<string, ElementPathNode> = normalizeElementPath(obj.elementPath);
  const own = (type: string): string | undefined => (obj.elementType === type ? obj.elementUuid : undefined);
  return {
    brandId: obj.brandId || path['BRAND']?.id || own('BRAND'),
    workspaceId: obj.workspaceId || path['WORKSPACE']?.id || own('WORKSPACE'),
    campaignId: obj.campaignId || path['CAMPAIGN']?.id || own('CAMPAIGN'),
  };
}

/** The notification service sends `elementProperties` as a JSON string, despite its declared shape. */
export function parseElementProperties(raw: unknown): Record<string, unknown> {
  let parsed: unknown = raw;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return {};
    }
  }
  return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {};
}

/** A comment left in the Drive surface: it names a message but no Drive address. */
export function isDriveComment(obj: NotificationObject): boolean {
  return !!obj.messageUuid && parseElementProperties(obj.elementProperties)['driveMode'] === true;
}

/** Id of the deepest directory in `elementPath` — a commented file's container. */
export function deepestDirectoryId(raw: RawElementPath): string | undefined {
  return Object.values(normalizeElementPath(raw))
    .filter((node: ElementPathNode) => !!node.id && !NON_DIRECTORY_ELEMENT_TYPES.has(node.type ?? ''))
    .reduce<ElementPathNode | undefined>(
      (deepest: ElementPathNode | undefined, node: ElementPathNode) =>
        deepest === undefined || Number(node.lv) > Number(deepest.lv) ? node : deepest,
      undefined
    )?.id;
}

/**
 * The Drive address of a notification's element: a file opens inside its
 * deepest ancestor folder, a folder opens itself, a project element stays on
 * the root.
 */
export function driveElementTarget(obj: NotificationObject): DriveSelectionTarget {
  const { type, uuid }: NotificationElement = notificationElement(obj);
  const isFile: boolean = type === 'FILE';
  const isDirectory: boolean = !!type && !NON_DIRECTORY_ELEMENT_TYPES.has(type);
  return {
    directoryUuid: isFile ? deepestDirectoryId(obj.elementPath) : isDirectory ? uuid : undefined,
    fileAliasUuid: isFile ? uuid : undefined,
  };
}

/** Element type at each fixed `elementProperties.lv` of the project hierarchy. */
const ELEMENT_TYPE_BY_LEVEL: Readonly<Record<number, string>> = {
  2: 'WORKSPACE',
  3: 'CAMPAIGN',
  4: 'PROJECT',
  5: 'SECTION',
  6: 'SUB-SECTION',
  7: 'FOLDER',
  8: 'SUB-FOLDER',
  9: 'VERSION',
  10: 'FILE',
};

export interface NotificationElement {
  readonly type?: string;
  readonly uuid?: string;
}

/**
 * The element a notification is about. Task notifications name it only through
 * `elementProperties` (`{ id, lv }`) — their `elementPath` holds just its ancestors.
 */
export function notificationElement(obj: NotificationObject): NotificationElement {
  if (obj.elementType) return { type: obj.elementType, uuid: obj.elementUuid };
  const { id, lv }: Record<string, unknown> = parseElementProperties(obj.elementProperties);
  return { type: ELEMENT_TYPE_BY_LEVEL[Number(lv)], uuid: typeof id === 'string' ? id : undefined };
}

/** The Drive address a comment notification resolves to, with the message focused in the feed. */
export function driveCommentTarget(obj: NotificationObject): DriveSelectionTarget {
  return {
    ...driveElementTarget(obj),
    messageUuid: obj.messageUuid,
    threadRootUuid: obj.parentMessageUuid ?? undefined,
  };
}
